import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Skeleton } from 'antd';
import { getTenantDetail } from '../api/tenant';
import { renewContract } from '../api/contract';
import type { RenewContractInput } from '../api/contract';
import { Topbar } from '../layouts/Topbar';
import { TermChips } from '../components/TermChips';
import { TenantStatusBadge } from '../components/StatusBadge';

const TODAY = new Date().toISOString().slice(0, 10);

function addDaysStr(yyyymmdd: string, days: number): string {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  return date.toISOString().slice(0, 10);
}
function addMonthsStr(yyyymmdd: string, months: number): string {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const date = new Date(y, m - 1 + months, d);
  if (date.getDate() < d) date.setDate(0);
  return date.toISOString().slice(0, 10);
}

function genContractNo(startAt: string): string {
  const now = new Date();
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  const t = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${pad(now.getMilliseconds(), 3)}`;
  return `HT-${startAt}-${t}`;
}

interface FormState {
  plan: string;
  contractStartAt: string;
  contractEndAt: string;
  graceDays: string;
  suspendDays: string;
  purgeDays: string;
  contractNo: string;
  notes: string;
}

const PLAN_OPTIONS = ['starter', 'pro', 'enterprise'] as const;

export function TenantRenewPage() {
  const { id } = useParams<{ id: string }>();
  const tenantId = Number(id);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenantDetail(tenantId),
    enabled: !Number.isNaN(tenantId),
  });

  // 窗口期默认值与"新建租户"保持一致（7/30/90）
  const [form, setForm] = useState<FormState>({
    plan: 'pro',
    contractStartAt: TODAY,
    contractEndAt: addMonthsStr(TODAY, 12),
    graceDays: '7',
    suspendDays: '30',
    purgeDays: '90',
    contractNo: '',
    notes: '',
  });
  const initializedRef = useRef(false);

  // 拿到当前合同后预填：开始日 = 当前合同到期 + 1 天（不可改），套餐沿用；窗口期保持 7/30/90
  useEffect(() => {
    if (initializedRef.current || !data?.currentContract) return;
    const cur = data.currentContract;
    const curEnd = cur.endAt?.slice(0, 10) ?? TODAY;
    const start = addDaysStr(curEnd, 1);
    const end = addMonthsStr(start, 12);
    setForm((prev) => ({
      ...prev,
      plan: cur.planCode ?? prev.plan,
      contractStartAt: start,
      contractEndAt: end,
      contractNo: genContractNo(start),
    }));
    initializedRef.current = true;
  }, [data?.currentContract]);

  const mutate = useMutation({
    mutationFn: (body: RenewContractInput) => renewContract(tenantId, body),
    onSuccess: () => {
      message.success('续签成功');
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      navigate(`/tenants/${tenantId}#contract`, { replace: true });
    },
    onError: (e: unknown) => {
      message.error(e instanceof Error ? e.message : '续签失败');
    },
  });

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // 续签场景：起始日不可改，只接受到期日变动（startAt 通过 onChange 传入但忽略）
  function handleTermChange({ endAt }: { startAt: string; endAt: string }) {
    setForm((prev) => ({ ...prev, contractEndAt: endAt }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.contractStartAt || !form.contractEndAt) { message.error('请选择合同到期日'); return; }
    if (form.contractEndAt <= form.contractStartAt) { message.error('合同到期必须晚于起始'); return; }
    if (!form.graceDays.trim() || Number(form.graceDays) < 0) { message.error('请填写宽限期天数'); return; }
    if (!form.suspendDays.trim() || Number(form.suspendDays) < 0) { message.error('请填写停服延迟天数'); return; }
    if (!form.purgeDays.trim() || Number(form.purgeDays) < 0) { message.error('请填写清退延迟天数'); return; }
    if (!form.contractNo.trim()) { message.error('请填写合同号'); return; }

    mutate.mutate({
      contractNo: form.contractNo,
      planCode: form.plan,
      startAt: form.contractStartAt,
      endAt: form.contractEndAt,
      graceDays: Number(form.graceDays),
      suspendDays: Number(form.suspendDays),
      purgeDays: Number(form.purgeDays),
      notes: form.notes || null,
    });
  }

  if (isLoading || !data) {
    return (
      <>
        <Topbar crumbs={[{ label: '运营' }, { label: '租户管理', href: '/tenants' }, { label: '…' }, { label: '续签' }]} />
        <div className="page"><Skeleton active /></div>
      </>
    );
  }

  const { tenant, currentContract } = data;
  const terminated = tenant.status === 'terminated';

  return (
    <>
      <Topbar crumbs={[
        { label: '运营' },
        { label: '租户管理', href: '/tenants' },
        { label: tenant.display_name, href: `/tenants/${tenantId}` },
        { label: '续签合同' },
      ]} />
      <div className="page">
        <div className="page-header">
          <div className="page-header__title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h1>续签合同</h1>
              <TenantStatusBadge status={tenant.status} />
            </div>
            <div className="page-header__sub">
              为租户 <span className="text-mono">{tenant.code}</span>（{tenant.display_name}）创建一份新合同。
              提交后系统会把当前合同标记 <span className="text-mono">is_current=FALSE</span>，新合同生效；
              若租户处于 grace / read_only / suspended，状态会自动恢复到 active。
            </div>
          </div>
        </div>

        {terminated && (
          <div className="alert alert--danger" style={{ marginBottom: 24 }}>
            <div>
              <div className="text-strong mb-2">⚠ 租户已清退</div>
              <div className="text-xs">数据库已被 DROP，无法续签。请走人工 pg_restore 流程恢复后再操作。</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="flex flex-col gap-6">
            {/* ① 当前合同（只读） */}
            <div className="card">
              <div className="card__header">
                <div className="card__title">① 当前合同</div>
                <span className="text-xs text-muted">仅作参考，提交后该合同标记为历史</span>
              </div>
              <div className="card__body">
                {currentContract ? (
                  <div className="meta-grid-3">
                    <div className="meta-grid__item">
                      <label>合同号</label>
                      <div className="val text-mono">{currentContract.contractNo ?? '—'}</div>
                    </div>
                    <div className="meta-grid__item">
                      <label>套餐</label>
                      <div className="val"><span className="tag">{currentContract.planCode}</span></div>
                    </div>
                    <div className="meta-grid__item">
                      <label>到期</label>
                      <div className="val text-mono">{currentContract.endAt?.slice(0, 10)}</div>
                    </div>
                    <div className="meta-grid__item">
                      <label>起始</label>
                      <div className="val text-mono">{currentContract.startAt?.slice(0, 10)}</div>
                    </div>
                    <div className="meta-grid__item">
                      <label>窗口期</label>
                      <div className="val">{currentContract.graceDays} / {currentContract.suspendDays} / {currentContract.purgeDays} 天</div>
                    </div>
                    <div className="meta-grid__item">
                      <label>状态</label>
                      <div className="val">{currentContract.isCurrent ? <span className="badge badge--active">CURRENT</span> : '—'}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted">该租户暂无当前合同，本次提交将作为首份续签合同写入。</div>
                )}
              </div>
            </div>

            {/* ② 新合同与窗口期 */}
            <div className="card">
              <div className="card__header">
                <div className="card__title">② 新合同与窗口期</div>
                <span className="text-xs text-muted">到期 → 宽限 → 只读 → 停服 → 清退</span>
              </div>
              <div className="card__body">
                <div className="fieldset">
                  <div className="field-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="field">
                      <label className="field__label" htmlFor="plan">套餐<span className="req">*</span></label>
                      <select id="plan" className="select" value={form.plan} onChange={(e) => set('plan', e.target.value)}>
                        {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>

                  <TermChips
                    startAt={form.contractStartAt}
                    endAt={form.contractEndAt}
                    onChange={handleTermChange}
                    defaultMonths={12}
                    startDisabled
                  />

                  <div className="field-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div className="field">
                      <label className="field__label" htmlFor="graceDays">宽限期<span className="req">*</span></label>
                      <div className="input-with-suffix">
                        <input id="graceDays" className="input" type="number" min={0} required value={form.graceDays}
                          onChange={(e) => set('graceDays', e.target.value)} />
                        <span className="input-suffix">天</span>
                      </div>
                      <div className="field__hint">到期 → 只读</div>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="suspendDays">停服延迟<span className="req">*</span></label>
                      <div className="input-with-suffix">
                        <input id="suspendDays" className="input" type="number" min={0} required value={form.suspendDays}
                          onChange={(e) => set('suspendDays', e.target.value)} />
                        <span className="input-suffix">天</span>
                      </div>
                      <div className="field__hint">只读 → 停服</div>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="purgeDays">清退延迟<span className="req">*</span></label>
                      <div className="input-with-suffix">
                        <input id="purgeDays" className="input" type="number" min={0} required value={form.purgeDays}
                          onChange={(e) => set('purgeDays', e.target.value)} />
                        <span className="input-suffix">天</span>
                      </div>
                      <div className="field__hint">停服 → DROP DATABASE</div>
                    </div>
                  </div>

                  <div className="field">
                    <label className="field__label" htmlFor="contractNo">合同号<span className="req">*</span></label>
                    <input id="contractNo" className="input" placeholder="HT-YYYY-MM-DD-HHmmssSSS" required
                      value={form.contractNo} onChange={(e) => set('contractNo', e.target.value)} />
                    <div className="field__hint">默认按合同起始日自动生成，可手动修改</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ③ 备注 */}
            <div className="card">
              <div className="card__header">
                <div className="card__title">③ 备注</div>
                <span className="text-xs text-muted">可选</span>
              </div>
              <div className="card__body">
                <div className="field">
                  <label className="field__label" htmlFor="notes">备注</label>
                  <textarea id="notes" className="textarea" rows={3}
                    value={form.notes} onChange={(e) => set('notes', e.target.value)} />
                  <div className="field__hint">用于记录本次续签的商务背景，例如：客户主动升级套餐、窗口期临时调整等</div>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bar">
            <button type="button" className="btn btn-ghost" onClick={() => navigate(`/tenants/${tenantId}`)}>取消</button>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span className="text-xs text-muted">提交后旧合同 is_current=FALSE，新合同立即生效</span>
              <button type="submit" className="btn btn-primary" disabled={mutate.isPending || terminated}>
                {mutate.isPending ? '提交中…' : '提交续签'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default TenantRenewPage;
